import { Fragment, useMemo, useState } from 'react';
import {Dialog, DialogPanel, DialogTitle, Transition} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { StorageImageItem } from '../types';

const FIELD = 'w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-800 shadow-sm outline-none transition focus:border-[#ee2b74] focus:ring-2 focus:ring-[#ee2b74]/20';

interface ImagePickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (url: string) => void;
    images: StorageImageItem[];
    loading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
}

export default function ImagePickerModal({isOpen, onClose, onSelect, images, loading, hasMore, onLoadMore,}: ImagePickerModalProps) {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<StorageImageItem | null>(null);

    const filtered = useMemo(() => {
        if (!query.trim()) return images;
        return images.filter(img => img.name.toLowerCase().includes(query.toLowerCase()));
    }, [query, images]);

    const handleClose = () => { setQuery(''); setSelected(null); onClose(); };

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={handleClose}>
                <Transition as={Fragment}
                                  enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
                                  leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm" />
                </Transition>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition as={Fragment}
                                          enter="ease-out duration-200" enterFrom="opacity-0 translate-y-3 scale-95" enterTo="opacity-100 translate-y-0 scale-100"
                                          leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-3 scale-95">
                            <DialogPanel className="w-full max-w-5xl overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl">
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
                                    <div>
                                        <DialogTitle className="text-lg font-semibold text-stone-900">Choose image from bucket</DialogTitle>
                                        <p className="mt-1 text-sm text-stone-500">Select an existing image from storage</p>
                                    </div>
                                    <button type="button" onClick={handleClose}
                                            className="rounded-lg p-2 text-stone-500 hover:bg-stone-100">
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 gap-0 lg:grid-cols-[1fr_320px]">
                                    {/* Image grid */}
                                    <div className="border-r border-stone-200 p-6">
                                        <div className="mb-4">
                                            <input type="text" value={query}
                                                   onChange={e => setQuery(e.target.value)}
                                                   placeholder="Search image filename…"
                                                   className={FIELD} />
                                        </div>

                                        {loading && images.length === 0 ? (
                                            <div className="py-10 text-center text-sm text-stone-500">Loading images…</div>
                                        ) : filtered.length === 0 ? (
                                            <div className="py-10 text-center text-sm text-stone-500">No images found.</div>
                                        ) : (
                                            <div className="grid max-h-[60vh] grid-cols-2 gap-4 overflow-y-auto pr-2 sm:grid-cols-3 md:grid-cols-4">
                                                {filtered.map(file => (
                                                    <button key={file.path} type="button"
                                                            onClick={() => setSelected(file)}
                                                            className={`group overflow-hidden rounded-xl border-2 bg-white text-left transition ${
                                                                selected?.path === file.path
                                                                    ? 'border-[#ee2b74] ring-2 ring-[#ee2b74]/20'
                                                                    : 'border-stone-200 hover:border-stone-300'
                                                            }`}>
                                                        <div className="aspect-square overflow-hidden bg-stone-100">
                                                            <img src={file.publicUrl} alt={file.name} loading="lazy"
                                                                 className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]" />
                                                        </div>
                                                        <div className="p-2">
                                                            <p className="truncate text-xs font-medium text-stone-800">{file.name}</p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {hasMore && !query.trim() && (
                                            <div className="mt-4">
                                                <button type="button" onClick={onLoadMore} disabled={loading}
                                                        className="w-full rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                                                    {loading ? 'Loading…' : 'Load more'}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Preview */}
                                    <div className="bg-stone-50 p-6">
                                        <p className="mb-3 text-sm font-semibold text-stone-900">Preview</p>
                                        {selected ? (
                                            <div className="space-y-3">
                                                <div className="aspect-square overflow-hidden rounded-xl border border-stone-200 bg-white">
                                                    <img src={selected.publicUrl} alt={selected.name}
                                                         className="h-full w-full object-cover" />
                                                </div>
                                                <div>
                                                    <p className="break-all text-sm font-medium text-stone-800">{selected.name}</p>
                                                    <p className="mt-1 break-all text-xs text-stone-500">{selected.path}</p>
                                                </div>
                                                <button type="button"
                                                        onClick={() => { onSelect(selected.publicUrl); handleClose(); }}
                                                        className="w-full rounded-lg bg-[#ee2b74] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#d4245f] transition">
                                                    Use this image
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="rounded-xl border border-dashed border-stone-300 bg-white p-6 text-center text-sm text-stone-500">
                                                Select a thumbnail to preview it here.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </DialogPanel>
                        </Transition>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}